'use client';

import { useState } from 'react';
import { PortableText } from '@portabletext/react';
import { interactiveObjectLinksPtComponents } from 'components/rich-text/pt-components';
import Divider from 'components/containers/divider';
import ContentSplit from 'components/containers/content-split';
import CollectionObject from 'components/collection/collection-object';
import LinkButton from 'components/controls/links/link-button';
import { lofiArtworkObjectModel } from 'helpers/collection';
import ChevronR from 'assets/icons/chevron-r.svg';
import { InteractiveObjectLinkContext } from './context';
import type { PortableTextBlock } from '@portabletext/types';
import type { SanityInteractiveObjectLink } from '@metmuseum/types';
import type { GraphQLObject } from '@metmuseum/types';
import styles from './interactive-object-links-rich-text.module.scss';

function formatObject(obj, imageOverride: string, imageOverrideAlt: string) {
	const formattedObject = lofiArtworkObjectModel(obj);

	if (imageOverride) {
		formattedObject.image = imageOverride;

		delete formattedObject.imageAlt;

		if (imageOverrideAlt) {
			formattedObject.imageAlt = imageOverrideAlt;
		}
	}

	return formattedObject;
}

function InteractiveObjectLinksRichText({
	value,
	objectLinks,
}: InteractiveObjectLinksRichTextProps) {
	const [activeKey, setActiveKey] = useState(objectLinks[0]?._key ?? null);
	const activeLink = objectLinks.find((link) => link._key === activeKey);

	return (
		<InteractiveObjectLinkContext.Provider value={{ activeKey, setActiveKey }}>
			<ContentSplit
				main={
					<div
						className={styles.text}
						data-testid="interactive-object-links-text"
					>
						<PortableText
							value={value}
							components={interactiveObjectLinksPtComponents}
						/>
					</div>
				}
				aside={
					<div className={styles.highlightedObject}>
						{activeLink && (
							<div key={activeLink._key} className={styles.objectWrapper}>
								<CollectionObject
									object={formatObject(
										activeLink.object,
										activeLink.imageOverride?.asset.url,
										activeLink.imageOverride?.alt,
									)}
									enforceMaxWidth={true}
									transparentBackground={!!activeLink.imageOverride?.asset.url}
								/>
								<Divider margin="var(--spacing-micro)" />
								<LinkButton
									text={activeLink.text}
									href={activeLink.searchLink}
									rightIcon={<ChevronR />}
								/>
							</div>
						)}
					</div>
				}
			/>
		</InteractiveObjectLinkContext.Provider>
	);
}

export interface InteractiveObjectLinksRichTextProps {
	value: PortableTextBlock[];
	objectLinks: (Omit<SanityInteractiveObjectLink, 'object'> & {
		object: GraphQLObject;
	})[];
}

export default InteractiveObjectLinksRichText;
